import { useState } from 'react';
import { Tags, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TagInput } from '@/components/TagInput';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { SKATE_SPOT_KIND } from '@/lib/types';
import { LoginArea } from '@/components/auth/LoginArea';

interface AddTagsToSpotProps {
  spotPubkey: string;
  spotIdentifier: string;
  spotTitle: string;
  existingTags?: string[];
}

export function AddTagsToSpot({ spotPubkey, spotIdentifier, spotTitle, existingTags = [] }: AddTagsToSpotProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  
  const spotRef = `${SKATE_SPOT_KIND}:${spotPubkey}:${spotIdentifier}`;
  
  const handleSubmit = async () => {
    if (tags.length === 0) {
      toast({
        title: 'No tags',
        description: 'Please add at least one tag.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      // Create kind 1111 comment with tag suggestions
      const eventTags: string[][] = [
        // Reference the spot with 'a' tag (NIP-22 comment target)
        ['a', spotRef],
        // Root marker for comment threading
        ['K', String(SKATE_SPOT_KIND)],
        // Mark this as a tag contribution
        ['t', 'skatespot-tags'],
      ];
      
      // Add all suggested tags
      for (const tag of tags) {
        eventTags.push(['t', tag]);
      }
      
      await publishEvent({
        kind: 1111,
        content: `Suggested tags for ${spotTitle}: ${tags.map(t => `#${t}`).join(' ')}`,
        tags: eventTags,
      });
      
      toast({
        title: 'Tags suggested!',
        description: 'Your tag suggestions have been submitted.',
      });
      
      // Reset and close
      setTags([]);
      setOpen(false);
      
      // Invalidate the tags query
      queryClient.invalidateQueries({ queryKey: ['spot-tag-suggestions', spotPubkey, spotIdentifier] });
    } catch (error) {
      toast({
        title: 'Failed to suggest tags',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    }
  };
  
  const handleClose = () => {
    setOpen(false);
    setTags([]);
  };
  
  // Filter out tags that already exist on the spot
  const handleTagChange = (newTags: string[]) => {
    // Remove any tags that are already on the spot
    const filteredTags = newTags.filter(tag => !existingTags.includes(tag));
    setTags(filteredTags);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Tags className="h-4 w-4" />
          Suggest Tags
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-[#ccff00]" />
            Suggest Tags
          </DialogTitle>
          <DialogDescription>
            Help categorize this spot by suggesting relevant tags.
          </DialogDescription>
        </DialogHeader>
        
        {!user ? (
          <div className="py-6 text-center">
            <p className="text-muted-foreground mb-4">Log in to suggest tags</p>
            <LoginArea className="justify-center" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing tags display */}
            {existingTags.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Current tags on this spot:</p>
                <div className="flex flex-wrap gap-1">
                  {existingTags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-gray-800 rounded-full text-gray-400">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tag input */}
            <div>
              <p className="text-sm font-medium mb-2">Add new tags:</p>
              <TagInput
                tags={tags}
                onChange={handleTagChange}
                placeholder="Type to search or add tags..."
                maxTags={10}
              />
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={tags.length === 0 || isPublishing}
                className="bg-[#ccff00] text-black hover:bg-[#ccff00]/90"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Tags className="h-4 w-4 mr-2" />
                    Suggest {tags.length} Tag{tags.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
